import React from "react";
import { Box, Typography, Button, Container, useTheme, useMediaQuery } from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Home, ArrowBack, Search } from "@mui/icons-material";
import { Images } from "../utils/Images";

const NotFoundPage = ({ message: propMessage }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  
  // Get message from location state (when navigated from CampaignDetailWrapper) or prop, default to "Page not found"
  const message = location.state?.message || propMessage || "Page not found";

  return (
      <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundColor: { xs: "transparent", lg: "#FAFAFA" },
        position: "relative",
        padding: { xs: "0", lg: "40px" },
        // Mobile and Tablet: Background image
        backgroundImage: {
          xs: `url(${Images.ourImpact})`,
          lg: "none",
        },
        backgroundSize: { xs: "cover", lg: "auto" },
        backgroundPosition: { xs: "center", lg: "center" },
        backgroundRepeat: { xs: "no-repeat", lg: "no-repeat" },
        // Custom breakpoint for 900px-1150px
        "@media (min-width: 900px) and (max-width: 1150px)": {
          backgroundColor: "transparent",
          backgroundImage: `url(${Images.ourImpact})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          padding: "20px",
        },
      }}
    >

      {/* Logo in top left corner */}
      <Box
        component={Link}
        to="/"
        sx={{
          position: "absolute",
          top: { xs: 20, md: 32 },
          left: { xs: 20, md: 32 },
          zIndex: 10,
          textDecoration: "none",
          "&:hover": {
            opacity: 0.8,
            transition: "opacity 0.3s ease",
          },
        }}
      >
        <Box
          component="img"
          src={Images.logo}
          alt="True Hope Foundation Logo"
          sx={{
            height: { xs: "40px", sm: "50px", md: "60px" },
            width: "auto",
            objectFit: "contain",
          }}
        />
      </Box>

      <Container 
        maxWidth="lg" 
        sx={{ 
          width: "100%", 
          position: "relative", 
          zIndex: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: { xs: "100vh", sm: "auto" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: { xs: "20px", sm: "24px", md: "28px", lg: "32px" },
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            padding: { xs: "20px", sm: "30px", lg: "0" },
            // Custom breakpoint for 900px-1150px
            "@media (min-width: 900px) and (max-width: 1150px)": {
              flexDirection: "column",
              padding: "20px",
            },
          }}
        >
          {/* White Card Container for Text and Buttons (Mobile, Tablet & Medium) */}
          <Box
            sx={{
              flex: { xs: "1 1 100%", lg: "1 1 50%" },
              textAlign: { xs: "center", lg: "left" },
              width: { xs: "100%", sm: "90%", lg: "auto" },
              backgroundColor: { xs: "#FFFFFF", lg: "transparent" },
              borderRadius: { xs: "16px", lg: "0" },
              padding: { xs: "32px 24px", sm: "40px 32px", lg: "0" },
              boxShadow: { 
                xs: "0 4px 20px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)", 
                lg: "none" 
              },
              maxWidth: { xs: "100%", sm: "600px", md: "650px", lg: "none" },
              // Custom breakpoint for 900px-1150px
              "@media (min-width: 900px) and (max-width: 1150px)": {
                backgroundColor: "#FFFFFF",
                borderRadius: "16px",
                padding: "40px 32px",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)",
                maxWidth: "700px",
                width: "90%",
              },
            }}
          >
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontFamily: "Red Hat Display, sans-serif",
                  fontWeight: 700,
                  fontSize: { 
                    xs: "clamp(4rem, 15vw, 6rem)", 
                    sm: "clamp(5rem, 12vw, 7rem)",
                    md: "clamp(5.5rem, 10vw, 7rem)",
                    lg: "clamp(6rem, 12vw, 8rem)" 
                  },
                  color: "#F04951",
                  lineHeight: 1,
                  marginBottom: { xs: "16px", sm: "20px", md: "22px", lg: "24px" },
                  letterSpacing: "-0.02em",
          }}
        >
          404
        </Typography>
        <Typography
          variant="h4"
                component="h2"
          sx={{
                  fontFamily: "Red Hat Display, sans-serif",
                  fontWeight: 700,
                  fontSize: { xs: "24px", sm: "28px", md: "30px", lg: "32px" },
                  color: "#1D1D1B",
                  marginBottom: { xs: "12px", sm: "14px", md: "15px", lg: "16px" },
                  lineHeight: 1.2,
          }}
        >
                Oops! {message}
        </Typography>
        <Typography
          variant="body1"
          sx={{
                  fontFamily: "Red Hat Display, sans-serif",
                  fontSize: { xs: "16px", sm: "17px", md: "17px", lg: "18px" },
                  color: "#666666",
                  marginBottom: { xs: "32px", sm: "36px", md: "38px", lg: "40px" },
            maxWidth: "500px",
                  marginLeft: { xs: "auto", lg: "0" },
                  marginRight: { xs: "auto", lg: "0" },
                  lineHeight: 1.6,
          }}
        >
                We're sorry, but the page you are looking for doesn't exist or has been moved. 
                Let's help you find what you're looking for and continue spreading hope together.
        </Typography>
        <Box
          sx={{
            display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: "12px", sm: "14px", md: "15px", lg: "16px" },
                  justifyContent: { xs: "center", lg: "flex-start" },
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="contained"
            startIcon={<Home />}
            onClick={() => navigate("/")}
            sx={{
                    backgroundColor: "#F04951",
                    color: "#FFFFFF",
                    borderRadius: "10px",
                    boxShadow: "0px 4px 0px #141414",
                    padding: { xs: "12px 20px", sm: "13px 22px", md: "14px 23px", lg: "15px 24px" },
                    height: { xs: "44px", sm: "46px", md: "47px", lg: "48px" },
                    fontFamily: "Red Hat Display, sans-serif",
                    fontWeight: 700,
                    fontSize: { xs: "14px", sm: "15px", md: "15px", lg: "16px" },
              textTransform: "none",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "#d63f46",
                      boxShadow: "0px 4px 0px #141414",
                    },
                    "&:active": {
                      boxShadow: "0px 2px 0px #141414",
                      transform: "translateY(2px)",
                    },
            }}
          >
                  Go to Homepage
          </Button>
          <Button
            variant="outlined"
                  startIcon={<Search />}
            onClick={() => navigate("/campaigns")}
            sx={{
                    borderColor: "#F04951",
                    color: "#F04951",
                    borderRadius: "10px",
                    borderWidth: "2px",
                    padding: { xs: "12px 20px", sm: "13px 22px", md: "14px 23px", lg: "15px 24px" },
                    height: { xs: "44px", sm: "46px", md: "47px", lg: "48px" },
                    fontFamily: "Red Hat Display, sans-serif",
                    fontWeight: 700,
                    fontSize: { xs: "14px", sm: "15px", md: "15px", lg: "16px" },
              textTransform: "none",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "#d63f46",
                      color: "#d63f46",
                      backgroundColor: "rgba(240, 73, 81, 0.04)",
                      borderWidth: "2px",
                    },
            }}
          >
            Browse Campaigns
          </Button>
              </Box>
          </Box>

          {/* Right Column - Image (Hidden on mobile, tablet & medium, shown on large desktop) */}
          <Box
            sx={{
              flex: { xs: "0 0 0", lg: "1 1 50%" },
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
              justifyContent: "center",
              padding: { lg: "40px" },
              // Custom breakpoint for 900px-1150px - hide image
              "@media (min-width: 900px) and (max-width: 1150px)": {
                display: "none",
              },
            }}
          >
            <Box
              component="img"
              src={Images.ourImpact}
              alt="True Hope Foundation Impact"
              sx={{
                width: "100%",
                maxWidth: "100%",
                height: "auto",
                objectFit: "contain",
                borderRadius: "16px",
              }}
            />
        </Box>
      </Box>
    </Container>
    </Box>
  );
};

export default NotFoundPage;
